import { useCallback, useEffect, useState } from 'react';
import type { UserEntity } from '@user/UserEntity';
import { FlatButton } from '@framework/ui/atoms';
import { Modal } from '@framework/ui/elements';
import { PublicIconUploader } from '@framework/utils/PublicIconUploader';
import { usePrevious } from '@view-model/models/common/hooks/usePrevious';
import { EditUserForm } from './EditUserForm';
import { EmailChangeModal } from './EmailChangeModal';
import { ConnectGoogleModal, DisconnectGoogleModal } from './google';
import { useGoogleLinked } from './google/useGoogleLinked';
import { ConnectMicrosoftModal, DisconnectMicrosoftModal } from './microsoft';
import { useMicrosoftLinked } from './microsoft/useMicrosoftLinked';
import { PasswordChangeModal, PasswordCreationModal } from './password';
import { ImageCropModal } from '@framework/ui/elements/ImageCropModal';
import { trpcClient } from '@framework/trpcClient';
import { useTranslation } from 'react-i18next';
type ModalName =
  | 'createPassword'
  | 'changePassword'
  | 'changeEmail'
  | 'connectGoogle'
  | 'disconnectGoogle'
  | 'connectMicrosoft'
  | 'disconnectMicrosoft';

type Props = {
  isOpen: boolean;
  onClose(): void;
  userEntity: UserEntity;
};

export const EditUserModal: React.FC<Props> = ({ isOpen, onClose, userEntity }: Props) => {
  const { t } = useTranslation();
  const { googleLinked, refreshGoogleLinked } = useGoogleLinked();
  const { microsoftLinked, refreshMicrosoftLinked } = useMicrosoftLinked();
  const [processing, setProcessing] = useState<boolean>(false);
  const [name, setName] = useState<string>(userEntity.name);
  const [imageUrl, setImageUrl] = useState<string | null>(userEntity.imageUrl);
  const previousIsOpen = usePrevious(isOpen);
  const [modalName, setModalName] = useState<ModalName | null>(null);
  const [iconImage, setIconImage] = useState<File | null>(null);
  const [uploadedImage, setUploadedImage] = useState<File | null>(null);
  const [isCropModalOpen, setIsCropModalOpen] = useState<boolean>(false);

  // isOpen が false から true に変化したときに、name を userEntity.name に設定する。
  useEffect(() => {
    if (isOpen && !previousIsOpen) {
      setName(userEntity.name);
      setImageUrl(userEntity.imageUrl);
    }
  }, [isOpen, previousIsOpen, userEntity]);

  useEffect(() => {
    if (uploadedImage) setIsCropModalOpen(true);
  }, [uploadedImage]);

  const handleChangeName = useCallback((name: string) => setName(name), []);

  const handleClose = useCallback(() => {
    setProcessing(false);
    setModalName(null);
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(async () => {
    if (name.length === 0) return;

    try {
      setProcessing(true);

      const newIconUrl = iconImage ? await PublicIconUploader.uploadUserIcon(userEntity.id, iconImage) : imageUrl;
      setImageUrl(newIconUrl);

      await trpcClient.user.editUserProfile.mutate({
        name,
        imageUrl: newIconUrl,
      });
      handleClose();
    } catch (e) {
      alert(t('auth.editUser.modal.updateError'));
      throw e;
    } finally {
      setProcessing(false);
    }
  }, [iconImage, userEntity.id, imageUrl, name, handleClose, t]);

  const handleCropModalClose = useCallback(() => {
    (document.getElementById('icon-image') as HTMLInputElement).value = '';
    setUploadedImage(null);
    setIsCropModalOpen(false);
  }, []);

  const handleCropModalSubmit = useCallback((image: File) => {
    setIconImage(image);
    setImageUrl(URL.createObjectURL(image));
    setUploadedImage(null);
    setIsCropModalOpen(false);
  }, []);

  const handleImageClear = useCallback(() => {
    setIconImage(null);
    setImageUrl(null);
  }, []);

  const handleUploadImage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) setUploadedImage(event.target.files[0]);
  }, []);

  const handleCreatePassword = useCallback(() => setModalName('createPassword'), []);
  const handleChangePassword = useCallback(() => setModalName('changePassword'), []);
  const handleChangeEmail = useCallback(() => setModalName('changeEmail'), []);
  const handleConnectGoogle = useCallback(() => setModalName('connectGoogle'), []);
  const handleDisconnectGoogle = useCallback(() => setModalName('disconnectGoogle'), []);
  const handleConnectMicrosoft = useCallback(() => setModalName('connectMicrosoft'), []);
  const handleDisconnectMicrosoft = useCallback(() => setModalName('disconnectMicrosoft'), []);
  const handleCloseSubModal = useCallback(() => setModalName(null), []);

  const handleCancelPendingMailAddressChange = useCallback(async () => {
    await trpcClient.user.cancelPendingMailAddressChange.mutate();
  }, []);

  if (modalName === 'createPassword') {
    return <PasswordCreationModal isOpen={isOpen} onClose={handleCloseSubModal} userEntity={userEntity} />;
  }

  if (modalName === 'changePassword') {
    return <PasswordChangeModal isOpen={isOpen} onClose={handleCloseSubModal} userEntity={userEntity} />;
  }

  if (modalName === 'changeEmail') {
    return <EmailChangeModal isOpen={isOpen} onClose={handleCloseSubModal} userEntity={userEntity} />;
  }

  if (modalName === 'connectGoogle') {
    return <ConnectGoogleModal isOpen={isOpen} onClose={handleCloseSubModal} userEntity={userEntity} />;
  }

  if (modalName === 'disconnectGoogle') {
    return (
      <DisconnectGoogleModal
        isOpen={isOpen}
        onClose={handleCloseSubModal}
        userEntity={userEntity}
        onDisconnected={refreshGoogleLinked}
      />
    );
  }

  if (modalName === 'connectMicrosoft') {
    return <ConnectMicrosoftModal isOpen={isOpen} onClose={handleCloseSubModal} userEntity={userEntity} />;
  }

  if (modalName === 'disconnectMicrosoft') {
    return (
      <DisconnectMicrosoftModal
        isOpen={isOpen}
        onClose={handleCloseSubModal}
        userEntity={userEntity}
        onDisconnected={refreshMicrosoftLinked}
      />
    );
  }

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose} className="flex flex-col">
        {/* header */}
        <div className="px-8 pb-4 pt-8 text-2xl font-bold">{t('auth.editUser.modal.title')}</div>
        <hr className="w-full" />

        {/* content */}
        <div className="flex flex-col p-8">
          <EditUserForm
            userEntity={userEntity}
            name={name}
            imageUrl={imageUrl}
            googleLinked={googleLinked}
            microsoftLinked={microsoftLinked}
            onChangeName={handleChangeName}
            onCreatePassword={handleCreatePassword}
            onChangePassword={handleChangePassword}
            onChangeEmail={handleChangeEmail}
            onCancelPendingMailAddressChange={handleCancelPendingMailAddressChange}
            onConnectGoogle={handleConnectGoogle}
            onDisconnectGoogle={handleDisconnectGoogle}
            onConnectMicrosoft={handleConnectMicrosoft}
            onDisconnectMicrosoft={handleDisconnectMicrosoft}
            onImageClear={handleImageClear}
            onUploadImage={handleUploadImage}
          />
        </div>

        {/* footer */}
        <hr className="w-full" />
        <div className="flex justify-end px-8 pb-8 pt-4">
          <FlatButton onClick={handleClose}>{t('common.action.close')}</FlatButton>
          <FlatButton
            color="brand"
            onClick={handleSubmit}
            className="ml-2 px-8"
            disabled={name === '' || processing}
            loading={processing}
          >
            {t('common.action.save')}
          </FlatButton>
        </div>
      </Modal>
      {uploadedImage && (
        <ImageCropModal
          isOpen={isCropModalOpen}
          onClose={handleCropModalClose}
          onSubmit={handleCropModalSubmit}
          src={uploadedImage}
        />
      )}
    </>
  );
};
